import { useState, useEffect, createContext, useContext } from 'react';
import axiosConfig from '../utils/axiosConfig';

// Contexto de Permissões
const PermissionsContext = createContext();

/**
 * Provider de Permissões
 * Gerencia o estado global das permissões do usuário
 */
export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Carregar permissões do usuário quando o provider é montado
  useEffect(() => {
    loadUserPermissions();
  }, []);

  const loadUserPermissions = async () => {
    try {
      // Verificar se há token
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      // Obter informações do usuário atual
      const userResponse = await axiosConfig.get('/users/profile');
      
      if (userResponse.data.success) {
        const userData = userResponse.data.data.user;
        setUser(userData);

        // Admin tem todas as permissões
        if (userData.role === 'admin') {
          setPermissions({
            isAdmin: true,
            hasFullAccess: true
          });
        } else {
          // Carregar permissões específicas do usuário
          // Para usuários comuns, as permissões são verificadas no backend
          // Aqui apenas marcamos que não é admin
          setPermissions({
            isAdmin: false,
            hasFullAccess: false
          });
        }
      }
    } catch (error) {
      console.error('Erro ao carregar permissões:', error);
      // Em caso de erro, assumir sem permissões
      setPermissions({});
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshPermissions = () => {
    setLoading(true);
    loadUserPermissions();
  };

  const logout = () => {
    setPermissions({});
    setUser(null);
    localStorage.removeItem('token');
  };

  const value = {
    permissions,
    user,
    loading,
    refreshPermissions,
    logout,
    // Helper functions
    isAdmin: () => permissions.isAdmin || false,
    hasFullAccess: () => permissions.hasFullAccess || false
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

/**
 * Hook para usar permissões
 */
export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  
  if (!context) {
    throw new Error('usePermissions deve ser usado dentro de um PermissionsProvider');
  }
  
  return context;
};

/**
 * Hook para verificar permissões específicas
 * Como as permissões granulares são verificadas no backend,
 * este hook foca na visibilidade de componentes
 */
export const usePermissionCheck = () => {
  const { permissions, user, loading } = usePermissions();

  const canAccess = (feature) => {
    if (loading) return false;
    
    // Admin sempre pode acessar tudo
    if (permissions.isAdmin) return true;
    
    // Verificações específicas por feature
    switch (feature) {
      case 'admin_panel':
        return permissions.isAdmin;
      
      case 'user_management':
        return permissions.isAdmin;
        
      case 'financial_config':
        // Para usuários comuns, isso será verificado no backend
        // Aqui apenas não bloqueamos a UI
        return true;
        
      case 'reports':
        return true; // Todos podem ver relatórios, mas dados serão filtrados no backend
        
      default:
        return true; // Por padrão, permitir (filtros no backend)
    }
  };

  const canManageUsers = () => canAccess('user_management');
  const isAdmin = () => permissions.isAdmin || false;
  const canAccessAdminPanel = () => canAccess('admin_panel');

  return {
    canAccess,
    canManageUsers,
    isAdmin,
    canAccessAdminPanel,
    user,
    loading
  };
};

/**
 * Componente HOC para proteger rotas baseado em permissões
 */
export const withPermission = (WrappedComponent, requiredFeature) => {
  return (props) => {
    const { canAccess, loading } = usePermissionCheck();

    if (loading) {
      return <div>Carregando...</div>;
    }

    if (!canAccess(requiredFeature)) {
      return (
        <div style={{ 
          padding: '40px', 
          textAlign: 'center',
          color: '#666'
        }}>
          <h3>Acesso Negado</h3>
          <p>Você não tem permissão para acessar esta página.</p>
        </div>
      );
    }

    return <WrappedComponent {...props} />;
  };
};

/**
 * Componente para mostrar conteúdo condicionalmente baseado em permissões
 */
export const PermissionGate = ({ feature, children, fallback = null }) => {
  const { canAccess, loading } = usePermissionCheck();

  if (loading) return null;

  return canAccess(feature) ? children : fallback;
};

export default usePermissions;